import "../HtmlEditor/HtmlEditor.scss";

import { Stack } from "@introist/react-foundation/v2";
import { useEditor, EditorContent } from "@tiptap/react";
import { useEffect } from "react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown } from "tiptap-markdown";
import styled from "styled-components";

import { FieldMarkdown } from "./Extensions/FieldMarkdown";
import { MarkdownToolbar } from "./Toolbar/MarkdownToolbar";

const Box = styled(Stack)<{ error?: boolean }>`
  padding: var(--spacing-small) var(--spacing-medium);
  background: ${props =>
    props.error ? "var(--palette-danger-ghosted)" : "var(--palette-surface-subdued)"};
  border-radius: var(--rounding-small);
`;

const EditorWrapper = styled(Box)`
  max-width: 458px;
  box-sizing: border-box;
  > div {
    flex-grow: 1;
    width: 100%;
  }
`;

const ConfiguredLink = Link.configure({
  openOnClick: false,
  protocols: ["https", "http"],
  autolink: true,
  linkOnPaste: true,
  defaultProtocol: "https"
});

interface MarkdownEditorProps {
  content: string;
  onChange?: (value: string) => unknown;
  placeholder?: string;
  error?: boolean;
  readonly?: boolean;
  workflowId?: string;
}

export const MarkdownEditor = ({
  content,
  onChange,
  placeholder,
  error,
  readonly = false,
  workflowId
}: MarkdownEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      FieldMarkdown,
      ConfiguredLink,
      Placeholder.configure({ placeholder }),
      Markdown.configure({ html: false, linkify: false, transformPastedText: true })
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange && onChange(editor.storage.markdown.getMarkdown());
    },
    editable: !readonly
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.storage.markdown.getMarkdown();
    if (current !== content) {
      editor.commands.setContent(content, false);
    }
  }, [content, editor]);

  return (
    <Stack vertical gap="xSmall">
      {!readonly && (
        <Box gap="xSmall">
          <MarkdownToolbar editor={editor} toolbarConfigurations={{ workflowId }} />
        </Box>
      )}
      <EditorWrapper justifyContent="flex-start" alignItems="flex-start" error={error}>
        <EditorContent editor={editor} />
      </EditorWrapper>
    </Stack>
  );
};
